import { PropsWithChildren, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { ConfigNumberParameters } from '@/data/libraries/ConfigParameters';
import { ShippingTypesCodes } from '@/data/libraries/ShippingTypes';

import { StatusCodes, getStatusList } from '@/data/libraries/Status';
import getTypeList from '@/data/libraries/IdentificationTypes';
import { useScreenSize, type ScreenSize } from '@/ui/hooks/useScreenSize';

import { ColumnDef } from '@tanstack/react-table';
import _, { isEmpty } from 'lodash';

import useDownloadExcel from '@/ui/hooks/useDownloadExcel';

import { Button, Col, Image, Row, Form } from 'react-bootstrap';

import { useAppSelector } from '@/ui/store/helperRedux';
import { normalize } from '@/domain/utils/normalize';

import { IOrderDetailResponse, IOrderResponse, IOrderStateRulesTransition, IOrderToStatusChange } from '@/domain/interfaces/IOrder';
import { ISelectOptionType } from '@/domain/interfaces/ISelect';
import { IShippingTypes } from '@/domain/interfaces/IProduct';
import { IStateFlow } from '@/domain/interfaces/IStatus';

import dateFormat from '@/domain/utils/dateFormat';
import currencyFormat from '@/domain/utils/currencyFormat';
import getTypeNameById from '@/domain/utils/getTypeById';
import getStatusById from '@/domain/utils/getStatusById';
import generateNumberId from '@/domain/utils/generateNumberId';
import { getCSSVariableValue } from '@/domain/utils/CssExtractVariable';
import { translate } from '@/domain/utils/translate';

import OrdersController from '@/controllers/OrdersController';

import Icon from '@/ui/assets/Icon';
import ErrorImage from '@/ui/assets/ErrorImage';

import MainTitle from '@/ui/components/MainTitle';
import TableCustom from '@/ui/components/TableCustom';
import ContentModal from '@/ui/components/modals/ContentModal';
import TwoButtonsGroup from '@/ui/components/TwoButtonsGroup';
import InputSelect from '@/ui/components/forms/InputSelect';
import InfoModal from '@/ui/components/modals/InfoModal';
import AlertModal from '@/ui/components/modals/AlertModal';
import GuideTip from '@/ui/components/GuideTip';
import InfoRow from '@/ui/components/InfoRow';
import InputForm from '@/ui/components/forms/InputForm';
import IconList from '@/ui/components/IconList';

import ManagementOptionsContainer from '@/ui/containers/managementOptions/ManagementOptionsContainer';

import DatePickerRange from '@/ui/pages/sales/DatePickerRange';

import './style.css'

/** componente de órdenes de venta */
const SalesHistory = () => {

  /** VARIABLES DE ENTORNO */
  const URL_IMAGES: string = process.env.REACT_APP_URL_IMAGES ?? '';

  const StatusList = getStatusList();

  /**Controlador para obtener las órdenes */
  const { getOrders, getOrdersDetails, sendOrderEmail, changeStatusOrder, getTransitionRules } = OrdersController();

  /** Trae la data del usuario registrado */
  const userLogued = useAppSelector((state) => state.userState);

  /** Referencia al input del motivo de cambio de estado */
  const statusReasonInputRef: React.MutableRefObject<HTMLInputElement | null> = useRef<HTMLInputElement | null>(null);

  /**Estado de error del input de motivo de cambio de estado */
  const [statusReasonInputWithError, setStatusReasonInputWithError] = useState<boolean>(false);

  /** Declaración inicial de los mensajes de error como vacíos para el campo de motivo de cambio de estado  */
  const [statusReasonInputMessageError, setStatusReasonInputMessageError] = useState<string>('');

  /** Estado que guarda las órdenes*/
  const [orders, setOrders] = useState<IOrderResponse[]>([]);

  /** Estado para mostrar el modal de confirmación de cambio de estado */
  const [showConfirmationStatusModal, setShowConfirmationStatusModal] = useState<boolean>(false);

  /**Estado para mostrar el modal de envío de correo */
  const [showSendEmailModal, setShowSendEmailModal] = useState<boolean>(false);

  /** guarda el id de la orden almacenada en el local para cambiar el estado */
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);

  /**Guarda todos los datos de la orden seleccionada  */
  const [selectedOrder, setSelectedOrder] = useState<IOrderResponse | null>(null);

  /** guarda el nuevo estado seleccionado para la orden seleccionada */
  const [selectedNewStatus, setSelectedNewStatus] = useState<number | null>(null);

  /**Modal de envío de correo */
  const [showLoadingEmailModal, setShowLoadingEmailModal] = useState<boolean>(false);

  /**Modal de loading */
  const [showDownloadModal, setShowDownloadModal] = useState<boolean>(false);

  /** Estado inicial del modal de estado de orden */
  const [alertStatus, setAlertStatus] = useState<boolean>(false);

  /** Estado inicial del tipo de modal para el estado de orden */
  const [alertStatusType, setAlertStatusType] = useState<'danger' | 'success' | undefined>(undefined);

  /** Estado inicial del modal de alertas de envío de correo*/
  const [alertEmail, setAlertEmail] = useState<boolean>(false);

  /** Estado inicial del tipo de modal para el envío de correo */
  const [alertEmailType, setAlertEmailType] = useState<'danger' | 'success' | undefined>(undefined);

  /** Lista de estados de la orden */
  const [statusList, setStatusList] = useState<IStateFlow[]>([]);
  /** Constantes de formato de fecha */
  const formatDateOne = 'dd/MM/yyyy';

  /** formato para enviarlo al back */
  const formatDateBackTwo = 'yyyy-MM-dd';

  /** formato para renderizado*/
  const formatDateTwo = 'dd-MM-yyyy';

  /** Función para formatear la fecha */
  const { formatDateToString: formatDateToStringOne, formatTimeToString } = dateFormat(formatDateOne);

  /** Función para formatear la fecha que se envia al back */
  const { formatDateToString: formatDateBackToString } = dateFormat(formatDateBackTwo);

  /**formato de fecha en el modal para renderizar */
  const { formatDateToString: formatDateToStringtwo } = dateFormat(formatDateTwo);

  /**Estado inicio de las fechas de entrega */
  const [startDate, setStartDate] = useState<Date | null>(null);

  /**Estado final de las fechas de entrega */
  const [endDate, setEndDate] = useState<Date | null>(null);

  /**Hook para la descarga del excel */
  const downloadExcelHook = useDownloadExcel();

  /** Estatus para validar con input*/
  const statusToValidate = [StatusCodes.ORDER_CANCELED];

  /** Hook que contiene las dimensiones de la pantalla y maneja los componentes de acuerdo al resultado obtenido */
  const { width, maxLargeWidth }: ScreenSize = useScreenSize();
  /** Variable que define si es entorno mobile o desktop */
  const isMobile: boolean = width < maxLargeWidth;
  /** Evento que muestra y oculta el modal de carga */
  const showLoading = (): void => {
    setShowDownloadModal(!showDownloadModal);
  };

  /** Prepara el objeto de los estados y su transición permitida */
  const getStatusListData = async (): Promise<void> => {
    /** Recupera la data de los estados de la librería */
    const statusUpdatedListRef: IStateFlow[] = [...StatusList];
    try {
      /** Extrae las reglas de transición de los estados desde el back */
      const rules: IOrderStateRulesTransition[] = await getTransitionRules(userLogued.user.token ?? '');
      if (rules.length > 0) {
        /** Actualiza las reglas en el objeto de estados */
        statusUpdatedListRef.forEach((status: IStateFlow) => {
          const statesAllowTransition: number[] = rules
            .filter((rule: IOrderStateRulesTransition) => {
              return status.shippingTypeId && rule.shippingType === status.shippingTypeId && rule.statusOrigin === status.id;
            })
            .map((ruleSelected: IOrderStateRulesTransition) => ruleSelected.statusDestiny);
          status.allowedTransitions?.splice(0, status.allowedTransitions.length, ...statesAllowTransition);
        });
      }
      setStatusList(statusUpdatedListRef);
    } catch (error) {
      setStatusList(statusUpdatedListRef);
    }
  };



  /** Definición de columnas */
  const columns: ColumnDef<IOrderResponse>[] = [
    {
      /** Id de transacción */
      accessorKey: 'id',
      header: () => <div className='text-center'>{COLUMN_ID}</div>,
      cell: (info) => {
        const idClient = info.renderValue() === null || (info.renderValue() as string).length === 0 ? 'S/I' : (info.renderValue() as string);
        return (
          <span className='d-flex justify-content-center my-2 p-large-regular bg-secondary-3 text-primary-4 badge rounded-pill mx-5'>{`${idClient}`}</span>
        );
      },
      footer: (info) => info.column.id,
    },
    {
      /** Código de la transacción */
      accessorKey: 'ordCodeNum',
      header: () => (
        <div className='text-center d-flex justify-content-center align-items-center'>
          {COLUMN_ID}

        </div>
      ),
      cell: (info) => {
        const idClient = info.renderValue() === null || (info.renderValue() as string).length === 0 ? 'S/I' : (info.renderValue() as string);
        return (
          <span className='d-flex justify-content-center my-2 p-large-regular bg-secondary-1 text-primary-4 badge rounded-pill mx-5'>{`${idClient}`}</span>
        );
      },
      footer: (info) => info.column.id,
    },
    {
      // Fecha de la orden
      accessorKey: 'dateFormatted',
      header: () => <div className='text-center'>{COLUMN_DATE}</div>,
      cell: (info) => {
        return (
          <span className='d-flex justify-content-center my-2 p-large-regular  text-secondary-2 w-100'>{`${formatDateToStringOne(
            info.renderValue() as Date,
          )}`}</span>
        );
      },
      footer: (info) => info.column.id,
    },
    {
      // Hora de la orden
      accessorKey: 'timeFormatted',
      header: () => <div className='text-center'>{COLUMN_HOUR}</div>,
      cell: (info) => {
        return (
          <span className='d-flex justify-content-center p-large-regular my-2  text-secondary-2 w-100'>{`${formatTimeToString(
            info.renderValue() as Date,
          )}`}</span>
        );
      },
      footer: (info) => info.column.id,
    },
    {
      // Nombre del cliente
      accessorKey: 'customerName',
      header: () => <div className='text-center'>{COLUMN_CLIENT_NAME}</div>,
      cell: (info) => {
        const nameClient = info.renderValue() === null || (info.renderValue() as string).length === 0 ? '' : (info.renderValue() as string);
        return <span className='d-flex justify-content-start p-large-regular my-2 text-secondary-2 w-100'>{`${nameClient}`}</span>;
      },
      footer: (info) => info.column.id,
    },

    {
      // Id del cliente
      accessorKey: 'customerId.value',
      header: () => <div className='text-center'>{COLUMN_ID_CLIENT}</div>,
      cell: (info) => {
        const idClient = info.renderValue();
        return (
          <span className='d-flex justify-content-center my-2 p-large-regular  text-secondary-2  w-100'>
            {!isEmpty(idClient) ? `${idClient}` : 'S/I'}
          </span>
        );
      },
      footer: (info) => info.column.id,
    },
    // tipo de entrega
    {
      accessorKey: 'ordShippingType',
      header: () => <div className='text-center'>{SHIPPING_TYPE}</div>,
      cell: (info) => {
        const shippingType: IShippingTypes = info.renderValue() as IShippingTypes;
        return (
          <div className='text-center'>
            {shippingType && (
              <GuideTip position='bottom' message={shippingType.title} key={`shippingType-${generateNumberId()}`}>
                <Icon key={shippingType.id} name={shippingType.icon} />
              </GuideTip>
            )}
          </div>
        );
      },
      footer: (info) => info.column.id,
    },
    {
      // Total de la orden
      accessorKey: 'totalAmount',
      header: () => <div className='text-center'>{COLUMN_TOTAL}</div>,

      cell: (info) => {
        return (
          <span className='d-flex justify-content-end my-2 p-large-regular  text-secondary-2  w-100'>{`${currencyFormat(
            info.renderValue() as number,
            ConfigNumberParameters.DECIMALS_ALLOWED,
          )}`}</span>
        );
      },
      footer: (info) => info.column.id,
    },
    {
      // Estado de la orden
      accessorKey: 'status',
      header: () => <div className='text-center'>{COLUMN_STATE}</div>,
      cell: (info) => {
        return <StatusCell status={info.renderValue() as number} id={info.row.original.id} orderData={info.row.original} statusList={statusList} />;
      },
      footer: (info) => info.column.id,
    },
    {
      // Posición de la orden
      accessorKey: 'position',
      header: () => <></>,
      cell: (info) => {
        return info.renderValue() as number;
      },
      footer: (info) => info.column.id,
    },
    {
      //Envío de correo electrónico
      accessorKey: 'send',
      header: () => <div className='text-center'>{COLUMN_SEND}</div>,
      cell: (info) => {
        const disabledStatuses = [StatusCodes.ORDER_PENDING, StatusCodes.ORDER_CANCELED, StatusCodes.ORDER_DELIVERED, StatusCodes.ORDER_ERROR];
        const disabled: boolean = disabledStatuses.includes(info.row.original.status ?? 0);
        return (
          <span className='d-flex justify-content-center mt-2'>
            <Icon
              name='ico-send'
              classMain={disabled ? 'svg-tertiary-1' : ''}
              onClick={disabled ? undefined : () => handleSelectOrderToSend(info.row.original)}
            />
          </span>
        );
      },
      footer: (info) => info.column.id,
    },
  ];

  const TypeList = getTypeList();

  /** mapeo de la lista de tipos de identificación */
  const identificationType = TypeList.find((type) => type.id === parseInt(selectedOrder?.customerId?.type ?? '0'));

  /** Validación de campo de confirmación de cambio de estado */
  const handleValidateChangeStatusReason = () => {
    const inputValue = statusReasonInputRef.current?.value ?? '';
    // const isPatternValid = validatePattern(BLANK_CHARACTER_AT_START, inputValue);
    if (statusToValidate.includes(selectedNewStatus ?? 0) && (inputValue.length < 1 || inputValue.length > 150 || inputValue.trim() === '')) {
      setStatusReasonInputWithError(true);
      setStatusReasonInputMessageError(INPUT_STATUS_CHANGE_ERROR);
    } else {
      setStatusReasonInputWithError(false);
      setStatusReasonInputMessageError('');
    }
  };

  /** Función que se ejecuta al escribir en el textArea */
  const handleChangeStatusReason = (e: React.ChangeEvent<HTMLInputElement>): void => {
    e.preventDefault();
    const inputValue = statusReasonInputRef.current ? e.target.value : '';
    statusReasonInputRef.current!.value = inputValue;
    handleValidateChangeStatusReason();
  };

  /**
   * Maneja el estado de la alerta según el resultado de la operación de envío de un correo electrónico.
   *
   * @param emailSent - Un booleano que indica si el correo electrónico se envió correctamente.
   * @param setType - Una función para actualizar el tipo de alerta, que puede ser 'danger' o 'success'.
   * @param setAlert - Una función para controlar la visibilidad de la alerta.
   *
   * @returns void
   *
   * Esta función actualiza el tipo de alerta a 'success' si el correo se envió correctamente,
   * de lo contrario, establece el tipo de alerta a 'danger'. También activa la visibilidad
   * de la alerta estableciendo el estado de alerta a true.
   */
  const handleAlert = (
    emailSent: boolean,
    setType: React.Dispatch<React.SetStateAction<'danger' | 'success' | undefined>>,
    setAlert: React.Dispatch<React.SetStateAction<boolean>>,
  ) => {
    if (emailSent) {
      setType('success');
    } else {
      setType('danger');
    }
    setAlert(true);
  };

  /** Función que maneja los estados de alerta del estado de la orden y su tipo */
  const handleAlertStatus = (emailSent: boolean) => {
    handleAlert(emailSent, setAlertStatusType, setAlertStatus);
  };

  /** Función que maneja los estados de alerta del envío de correo y su tipo */
  const handleAlertEmail = (emailSent: boolean) => {
    handleAlert(emailSent, setAlertEmailType, setAlertEmail);
  };

  /** Función que confirma el cambio de estado */
  const confirmStatusChange = () => {
    if (selectedOrderId && selectedNewStatus) {
      changeStatus({
        orderId: selectedOrderId,
        cause: statusReasonInputRef.current?.value ?? '',
        statusId: selectedNewStatus,
        initialDeliveryDate: startDate ? formatDateBackToString(startDate) : null,
        finalDeliveryDate: endDate ? formatDateBackToString(endDate) : null,
      });
    }
    setShowConfirmationStatusModal(false);
    cleanDateAndClose();
  };

  /** Función que actualiza el estado del componente con los valores de start y end respectivamente. */
  const handleDateChange = (start: Date | null, end: Date | null) => {
    setStartDate(start);
    setEndDate(end);
  };

  /** Estado para mostrar el modal de filtro por código de orden */
  const [showFilterModal, setShowFilterModal] = useState<boolean>(false);

  /** Estado para guardar el valor del filtro por código de orden */
  const [orderCodeFilter, setOrderCodeFilter] = useState<string>('');

  /** Estado para guardar las órdenes sin filtrar */
  const [unfilteredOrders, setUnfilteredOrders] = useState<IOrderResponse[]>([]);

  /** Referencia para el input del filtro */
  const orderCodeFilterRef = useRef<HTMLInputElement | null>(null);

  /** Estado para controlar si hay un filtro activo */
  const [isFilterActive, setIsFilterActive] = useState<boolean>(false);
  /** Estado para los distintos filtros*/
  const [statusFilter, setStatusFilter] = useState<number[] | null>(null);
  /** Estado para el filtro por tipo de entrega (booleano) */
  const [deliveryTypeFilter, setDeliveryTypeFilter] = useState<number[] | null>(null);
  /** Estado para el buscador*/
  const [globalSearch, setGlobalSearch] = useState('');
  // Estados para los filtros temporales del modal
  const [tempStatusFilter, setTempStatusFilter] = useState<number[] | null>(null);
  const [tempDeliveryTypeFilter, setTempDeliveryTypeFilter] = useState<number[] | null>(null);

  /** Función que trae las órdenes */
  const getOrdersBack = async (): Promise<void> => {
    await getOrders(userLogued.user.token ?? '').then((order: IOrderResponse[] | null) => {
      if (order !== null) {
        const sortedOrders = order.sort((a, b) => {
          const dateA = new Date(a.dateFormatted as string).getTime();
          const dateB = new Date(b.dateFormatted as string).getTime();
          return dateA - dateB;
        });

        setUnfilteredOrders(sortedOrders);

        // Si hay filtros activos, reaplica el filtro
        if (
          (statusFilter && statusFilter.length > 0) ||
          (deliveryTypeFilter && deliveryTypeFilter.length > 0)
        ) {
          filterOrders(sortedOrders);
          setIsFilterActive(true);
        } else {
          setOrders(order);
          setIsFilterActive(false);
        }

        setOrderCodeFilter('');
      }
    });
  };
  const filterOrders = (ordersList: IOrderResponse[]) => {
    let filtered = [...ordersList];
    if (statusFilter && statusFilter.length > 0) {
      filtered = filtered.filter(order =>
        order.status !== undefined && statusFilter.includes(order.status)
      );
    }
    if (deliveryTypeFilter && deliveryTypeFilter.length > 0) {
      filtered = filtered.filter(order =>
        order.ordShippingType?.id !== undefined &&
        deliveryTypeFilter.includes(order.ordShippingType.id)
      );
    }
    setOrders(filtered);
  };
  /** Extrae una lista única de tipos de envío a partir de la lista de órdenes */
  const uniqueShippingTypes: IShippingTypes[] = Array.from(
    new Map(
      unfilteredOrders
        .map(order => order.ordShippingType)
        .filter((type): type is IShippingTypes => !!type)
        .map(type => [type.id, type])
    ).values()
  );

  /** Función para abrir el modal de filtro */
  const openFilterModal = (): void => {
    setTempStatusFilter(statusFilter ? [...statusFilter] : null);
    setTempDeliveryTypeFilter(deliveryTypeFilter ? [...deliveryTypeFilter] : null);
    setShowFilterModal(true);
    setTimeout(() => {
      if (orderCodeFilterRef.current) {
        orderCodeFilterRef.current.focus();
      }
    }, 300);
  };

  /** Funcion para que al cerrar el modal sin aplicar, limpia los filtros temporales (sin aplicar) */
  const closeFilterModalAndResetTemp = (): void => {
    setShowFilterModal(false);
    setTempStatusFilter(null);
    setTempDeliveryTypeFilter(null);
  };


  /** Función para cerrar el modal de filtro */
  const closeFilterModal = (): void => {
    setShowFilterModal(false);
  };

  /** Función para actualizar el valor del filtro */
  const handleFilterInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;
    setOrderCodeFilter(value);
  };

  /** Función para aplicar el filtro */
  const applyFilter = (): void => {
    const normalizedStatus = tempStatusFilter && tempStatusFilter.length > 0 ? [...tempStatusFilter] : null;
    const normalizedDelivery = tempDeliveryTypeFilter && tempDeliveryTypeFilter.length > 0 ? [...tempDeliveryTypeFilter] : null;

    setStatusFilter(normalizedStatus);
    setDeliveryTypeFilter(normalizedDelivery);

    applyAllFilters(unfilteredOrders, globalSearch, normalizedStatus, normalizedDelivery);
    closeFilterModal();
  };
  /** Función para limpiar el filtro */
  const clearFilter = (): void => {
    setStatusFilter(null);
    setDeliveryTypeFilter(null);
    setTempStatusFilter(null);
    setTempDeliveryTypeFilter(null);
    applyAllFilters(unfilteredOrders, globalSearch, null, null);
    setIsFilterActive(globalSearch !== '');
  };

  /**
   * Aplica múltiples filtros a una lista de pedidos y actualiza el estado de los pedidos filtrados.
   *
   * @param ordersList - Lista de órdenes a filtrar. Por defecto es `unfilteredOrders`.
   * @param search - Cadena de búsqueda global para filtrar por nombre del cliente, código de orden o cédula del cliente. Por defecto es `globalSearch`.
   * @param status - Arreglo de códigos de estado para filtrar las órdenes por su estado. Si es `null` o vacío, no se aplica filtro de estado. Por defecto es `statusFilter`.
   * @param delivery - Arreglo de IDs de tipo de entrega para filtrar las órdenes por su tipo de envío. Si es `null` o vacío, no se aplica filtro de tipo de entrega. Por defecto es `deliveryTypeFilter`.
   *
   * La función aplica los siguientes filtros en orden:
   * 1. Filtro por estado: Mantiene las órdenes cuyo estado está incluido en el arreglo `status`.
   * 2. Filtro por tipo de entrega: Mantiene las órdenes cuyo ID de tipo de envío está incluido en el arreglo `delivery`.
   * 3. Filtro de búsqueda global: Mantiene las órdenes donde el nombre del cliente, el código de orden o la cédula del cliente incluyen todas las palabras de la búsqueda normalizada.
   *
   * Después de filtrar, actualiza el estado de las órdenes y marca si hay filtros activos.
   */
  const applyAllFilters = (
    ordersList: IOrderResponse[] = unfilteredOrders,
    search: string = globalSearch,
    status: number[] | null = statusFilter,
    delivery: number[] | null = deliveryTypeFilter
  ) => {
    let filtered = [...ordersList];

    // Filtro por estado
    if (status && status.length > 0) {
      filtered = filtered.filter(order =>
        order.status !== undefined && status.includes(order.status)
      );
    }

    // Filtro por tipo de entrega
    if (delivery && delivery.length > 0) {
      filtered = filtered.filter(order =>
        order.ordShippingType?.id !== undefined &&
        delivery.includes(order.ordShippingType.id)
      );
    }

    // Filtro por búsqueda global
    const searchNormalized = normalize(search);
    if (searchNormalized !== '') {
      const searchWords = searchNormalized.split(' ');
      filtered = filtered.filter(order => {
        const name = normalize(order.customerName || '');
        const code = normalize(order.ordCodeNum || '');
        let doc = normalize(order.customerId?.value || '');
        if (!doc) doc = 's/i';
        return searchWords.every(word =>
          name.includes(word) || code.includes(word) || doc.includes(word)
        );
      });
    }

    setOrders(filtered);
    setIsFilterActive(
      (status && status.length > 0) ||
      (delivery && delivery.length > 0) ||
      searchNormalized !== ''
    );
  };

  //función que envía el email de la orden seleccionada
  const sendOrder = async (): Promise<void> => {
    if (selectedOrder) {
      // Mostrar el modal de carga
      setShowSendEmailModal(false);
      setShowLoadingEmailModal(true);

      await sendOrderEmail(selectedOrder.ordCodeNum, userLogued.user.token ?? '').then((value: boolean) => {
        // Ocultar el modal de carga
        setShowLoadingEmailModal(false);
        handleAlertEmail(value);
      });
    }
  };

  //Función para cambio de estado de la orden
  const changeStatus = async (order: IOrderToStatusChange): Promise<void> => {
    if (!selectedOrder) return;

    const validStatuses = new Set([
      StatusCodes.ORDER_PROCESSING,
      StatusCodes.ORDER_BRANCH_PICKUP,
      StatusCodes.ORDER_SENT,
      StatusCodes.ORDER_DELIVERED,
      StatusCodes.ORDER_CANCELED,
    ]);
    const token = userLogued.user.token ?? '';

    // Mostrar el modal de carga
    setShowConfirmationStatusModal(false);

    if (selectedNewStatus !== null && validStatuses.has(selectedNewStatus)) {
      setShowLoadingEmailModal(true);
    }

    try {
      const value = await changeStatusOrder(order, token);
      getOrdersBack();
      handleAlertStatus(value);
    } catch (error) {
      console.error('Error al cambiar el estado:', error);
    } finally {
      setShowLoadingEmailModal(false);
    }
  };

  /** Evento que muestra y oculta el modal de envío de correo */
  const handleSelectOrderToSend = (order: IOrderResponse): void => {
    setSelectedOrder(order);
    setShowSendEmailModal(true);
  };

  /** Evento que muestra y oculta el modal de carga */
  const handleSendEmail = (): void => {
    setShowLoadingEmailModal(!showLoadingEmailModal);
  };

  const handleDeliveryTypeChange = ({
    e,
    typeId,
    uniqueShippingTypes,
    setTempDeliveryTypeFilter,
    setTempStatusFilter,
    ShippingTypesCodes,
    StatusCodes
  }: {
    e: React.ChangeEvent<HTMLInputElement>,
    typeId: number,
    uniqueShippingTypes: { id: number }[],
    setTempDeliveryTypeFilter: React.Dispatch<React.SetStateAction<number[] | null>>,
    setTempStatusFilter: React.Dispatch<React.SetStateAction<number[] | null>>,
    ShippingTypesCodes: any,
    StatusCodes: any
  }) => {
    const isChecked = e.target.checked;

    setTempDeliveryTypeFilter(prev => {
      let next;
      if ((prev === null || prev.length === 0) && !isChecked) {
        const allIds = uniqueShippingTypes.map(t => t.id);
        next = allIds.filter(id => id !== typeId);
      } else if ((prev === null || prev.length === 0) && isChecked) {
        next = null;
      } else if (isChecked) {
        const newArr = [...(prev || []), typeId];
        if (newArr.length === uniqueShippingTypes.length) {
          next = null;
        } else {
          next = newArr;
        }
      } else {
        next = (prev || []).filter(id => id !== typeId);
      }

      // Reglas para modificar el estado del filtro de estado según el tipo de envío seleccionado
      setTempStatusFilter(prevStatus => {
        if (
          Array.isArray(next) &&
          next.length === 1 &&
          next[0] === ShippingTypesCodes.NATIONAL_SHIPMENT
        ) {
          return (prevStatus || []).filter(id => id !== StatusCodes.ORDER_BRANCH_PICKUP);
        }
        if (
          Array.isArray(next) &&
          next.length === 1 &&
          next[0] === ShippingTypesCodes.PICK_UP
        ) {
          return (prevStatus || []).filter(id => id !== StatusCodes.ORDER_SENT);
        }
        return prevStatus;
      });

      return next;
    });
  };

  const handleStatusChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    statusId: number,
    setTempStatusFilter: React.Dispatch<React.SetStateAction<number[] | null>>
  ): void => {
    const isChecked = e.target.checked;

    setTempStatusFilter(prev =>
      isChecked
        ? [...(prev || []), statusId]
        : (prev || []).filter(id => id !== statusId)
    );
  };

  /**
   * Elimina un filtro de estado por su ID, actualiza el estado del filtro 
   * y aplica los filtros actualizados a la lista de pedidos.
   */
  const removeStatusFilter = (id: number) => {
    const newStatus = (statusFilter ?? []).filter(s => s !== id);
    setStatusFilter(newStatus.length ? newStatus : null);
    setTempStatusFilter(newStatus.length ? newStatus : null); // Actualiza el modal

    // Aplica los filtros actualizados a la lista usando applyAllFilters para mantener la búsqueda global
    applyAllFilters(
      unfilteredOrders,
      globalSearch,
      newStatus.length ? newStatus : null,
      deliveryTypeFilter && deliveryTypeFilter.length > 0 ? deliveryTypeFilter : null
    );
  };

  /**
    * Elimina un filtro de tipo de entrega por su ID, actualiza el estado del filtro 
    * y aplica los filtros actualizados a la lista de pedidos.
   */

  const removeDeliveryTypeFilter = (id: number) => {
    const newDelivery = (deliveryTypeFilter ?? []).filter(t => t !== id);
    setDeliveryTypeFilter(newDelivery.length ? newDelivery : null);
    setTempDeliveryTypeFilter(newDelivery.length ? newDelivery : null);

    // Usa applyAllFilters para mantener la búsqueda global y otros filtros
    applyAllFilters(
      unfilteredOrders,
      globalSearch,
      statusFilter,
      newDelivery.length ? newDelivery : null
    );
  };

  const ActiveFiltersBadges = ({
    statusFilter,
    deliveryTypeFilter,
    uniqueShippingTypes,
    buttonVariant,
    clearFilter,
    FILTERING_BY_TEXT,
    FILTER_MODAL_CLEAR,
  }) => (
    <>
      <span className='text-secondary-2 me-2'>{FILTERING_BY_TEXT}</span>
      {statusFilter !== null && (statusFilter ?? []).map(id => (
        <span key={id} className='badge bg-primary-4 text-black me-2 mb-2 mt-2 d-inline-flex align-items-center'>
          {getStatusById(id).replace(/\b\w/g, c => c.toUpperCase())}
          <Button
            size='sm'
            className='p-0 text-black btn-close'
            style={{ background: 'none', border: 'none', boxShadow: 'none' }}
            onClick={() => removeStatusFilter(id)}
            aria-label='Eliminar filtro de estado'
          >
            <span aria-hidden='true'>&times;</span>
          </Button>
        </span>
      ))}
      {deliveryTypeFilter !== null && (deliveryTypeFilter ?? []).map(id => {
        const type = uniqueShippingTypes.find(t => t.id === id);
        if (!type) return null;
        return (
          <span key={id} className='badge bg-primary-4 text-black me-2 mb-2 mt-2 d-inline-flex align-items-center'>
            {type.title}
            <Button
              size='sm'
              className='p-0 text-black btn-close'
              style={{ background: 'none', border: 'none', boxShadow: 'none' }}
              onClick={() => removeDeliveryTypeFilter(id)}
              aria-label='Eliminar filtro de tipo de entrega'
            >
              <span aria-hidden='true'>&times;</span>
            </Button>
          </span>
        );
      })}
      <Button className={`${buttonVariant} ms-2 mb-2 mt-2`} onClick={clearFilter}>
        {FILTER_MODAL_CLEAR}
      </Button>
    </>
  );
  const StatusFilterOptions = () => {
    const statusOrder = [
      StatusCodes.ORDER_PENDING, // Pendiente
      StatusCodes.ORDER_PROCESSING, // En Proceso
      StatusCodes.ORDER_SENT, // Enviado
      StatusCodes.ORDER_BRANCH_PICKUP, // En Sucursal
      StatusCodes.ORDER_DELIVERED, // Entregado
      StatusCodes.ORDER_CANCELED, // Cancelado
      StatusCodes.ORDER_ERROR // Error
    ];
    const filteredStatuses = [
      ...new Map(
        statusList
          .filter(
            status =>
              status.shippingTypeId === ShippingTypesCodes.NATIONAL_SHIPMENT ||
              status.shippingTypeId === ShippingTypesCodes.PICK_UP
          )
          .sort((a, b) => {
            // Ordena según el array statusOrder
            return statusOrder.indexOf(a.id) - statusOrder.indexOf(b.id);
          })
          .map(status => [status.id, status])
      ).values()
    ];
    const STATE: string = translate('sales.state');

    // Lógica para manejar el cambio de los checkboxes de estado
    const handleStatusChange = (
      e: React.ChangeEvent<HTMLInputElement>,
      statusId: number
    ): void => {
      const isChecked = e.target.checked;
      setTempStatusFilter(prev =>
        isChecked
          ? [...(prev || []), statusId]
          : (prev || []).filter(id => id !== statusId)
      );
    };

    return (
      <div className='mb-4'>
        <h5 className='p-regular-medium mb-3'>{STATE}</h5>
        {filteredStatuses.map(status => {
          let disabled = false;

          if (
            tempDeliveryTypeFilter &&
            tempDeliveryTypeFilter.length === 1 &&
            tempDeliveryTypeFilter[0] === ShippingTypesCodes.NATIONAL_SHIPMENT &&
            status.id === StatusCodes.ORDER_BRANCH_PICKUP
          ) {
            disabled = true;
          }

          if (
            tempDeliveryTypeFilter &&
            tempDeliveryTypeFilter.length === 1 &&
            tempDeliveryTypeFilter[0] === ShippingTypesCodes.PICK_UP &&
            status.id === StatusCodes.ORDER_SENT
          ) {
            disabled = true;
          }

          return (
            <div
              key={`status-${status.id}`}
              className='form-check form-switch mb-2 d-flex justify-content-between'
            >
              <label
                htmlFor={`status-${status.id}`}
                className={`form-check-label p-regular me-2 ${disabled ? 'text-muted' : ''}`}
              >
                {status.name.toUpperCase()}
              </label>
              <Form.Check.Input
                className='form-check-input'
                type='checkbox'
                id={`status-${status.id}`}
                checked={tempStatusFilter?.includes(status.id) || false}
                disabled={disabled}
                onChange={e => handleStatusChange(e, status.id)}
              />
            </div>
          );
        })}
      </div>
    );
  };
  const DeliveryTypeFilterOptions = () => (
    <div className='mb-4'>
      <h5 className='p-regular-medium mb-3'>{SHIPPING_TYPE_TEXT}</h5>
      {uniqueShippingTypes.map((type) => {
        const isActive =
          tempDeliveryTypeFilter === null || tempDeliveryTypeFilter.length === 0
            ? true
            : tempDeliveryTypeFilter.includes(type.id);
        return (
          <div
            key={`shipping-type-${type.id}`}
            className='form-check form-switch mb-2 d-flex justify-content-between'
          >
            <label
              className='form-check-label p-regular me-2'
              htmlFor={`delivery-type-${type.id}`}
            >
              {type.title.toUpperCase()}
            </label>
            <Form.Check.Input
              className='form-check-input'
              type='checkbox'
              id={`delivery-type-${type.id}`}
              checked={isActive}
              onChange={(e) =>
                handleDeliveryTypeChange({
                  e,
                  typeId: type.id,
                  uniqueShippingTypes,
                  setTempDeliveryTypeFilter,
                  setTempStatusFilter,
                  ShippingTypesCodes,
                  StatusCodes,
                })
              }
            />
          </div>
        );
      })}
    </div>
  );
  const handleGlobalSearch = (value: string) => {
    const search = normalize(value);
    if (search === '') {
      setOrders(unfilteredOrders);
      setIsFilterActive(false);
      return;
    }

    const searchWords = search.split(' ');

    const filtered = unfilteredOrders.filter(order => {
      const name = normalize(order.customerName || '');
      const code = normalize(order.ordCodeNum || '');
      let doc = normalize(order.customerId?.value || '');
      if (!doc) doc = 's/i';

      return searchWords.every(word =>
        name.includes(word) || code.includes(word) || doc.includes(word)
      );
    });

    setOrders(filtered);
    setIsFilterActive(true);
  };
  const columnTranslations = {
    order_code: translate('sales.columns.order_code'),
    client_name: translate('sales.columns.client_name'),
    client_type: translate('sales.columns.client_type'),
    client_id: translate('sales.columns.client_id'),
    client_phone: translate('sales.columns.client_phone'),
    client_email: translate('sales.columns.client_email'),
    shipping_address: translate('sales.columns.shipping_address'),
    shipping_types: translate('sales.columns.shipping_types'),
    shipping_cost: translate('sales.columns.shipping_cost'),
    shipppng_initial_date: translate('sales.columns.shipppng_initial_date'),
    shipppng_final_date: translate('sales.columns.shipppng_final_date'),
    total_amount: translate('sales.columns.total_amount'),
    order_status: translate('sales.columns.order_status'),
    order_date: translate('sales.columns.order_date'),
    order_time: translate('sales.columns.order_time'),
    authoritation_code: translate('sales.columns.authoritation_code'),
    pay_method: translate('sales.columns.pay_method'),
    installments_to_pay: translate('sales.columns.installments_to_pay'),
    amount_per_installment: translate('sales.columns.amount_per_installment'),
    card_number: translate('sales.columns.card_number'),
    payment_status: translate('sales.columns.payment_status'),
    sku: translate('sales.columns.sku'),
    product_name: translate('sales.columns.product_name'),
    product_price: translate('sales.columns.product_price'),
    quantity: translate('sales.columns.quantity'),
    product_total: translate('sales.columns.product_total'),
  };

  /** Función que trae las órdenes para la descarga del excel */
  const getOrdersDetailsBack = async (): Promise<void> => {
    setShowDownloadModal(true);
    const Excel: any[] = [];
    await getOrdersDetails(userLogued.user.token ?? '').then((order: IOrderDetailResponse[] | null) => {
      if (order !== null) {
        order.forEach((row) => {
          const transformedRow = {
            order_code: row.ordCodeNum,
            client_name: row.customerName,
            client_type: getTypeNameById(parseInt(row.customerTypeId)),
            client_id: row.customerId,
            client_phone: row.customerPhone,
            client_email: row.customerEmail,
            shipping_address: row.deliveryAddress,
            shipping_types: row.shippingTypeTitle,
            shipping_cost: row.ordTotalShipping,
            shipppng_initial_date: row.ordInitialDeliveryDate,
            shipppng_final_date: row.ordFinalDeliveryDate,
            total_amount: row.orderTotalAmount,
            order_status: getStatusById(row.orderStatus),
            order_date: row.orderDate,
            order_time: row.orderTime,
            authoritation_code: row.authId,
            pay_method: row.payType,
            installments_to_pay: row.amountOfFees,
            amount_per_installment: row.amountPaidPerFee,
            card_number: row.cardNumber,
            payment_status: getStatusById(row.paymentStatus),
            sku: row.productSku,
            product_name: row.productName,
            product_price: row.productPrice,
            quantity: row.productQuantity,
            product_total: row.productTotalAmount,
          };

          Excel.push(transformedRow);
        });
        const sortedData = _.sortBy(Excel, ['order_code']);
        downloadExcelHook.download(sortedData, EXCEL_FILE_NAME, columnTranslations);
        // Usar setTimeout para esperar 1 segundo antes de cerrar el modal
        setTimeout(() => {
          setShowDownloadModal(false);
        }, 1000); // Espera 1 segundo
      }
    });
  };

  /**CONSTANTES DE TEXTO */
  const LOADING_IMAGE_URL: string = `${URL_IMAGES}/client/send-email.png`;
  const DOWNLOAD_IMAGE_URL: string = `${URL_IMAGES}/client/doc-download.png`;
  const CHANGE_BUTTON_TEXT: string = translate('sales.change_button_text');
  const INPUT_STATUS_CHANGE_LABEL: string = translate('sales.input_status_change_label');
  const COLUMN_ID: string = translate('sales.order_number');
  const COLUMN_STATE: string = translate('sales.state');
  const COLUMN_TOTAL: string = translate('sales.total');
  const COLUMN_DATE: string = translate('sales.date');
  const COLUMN_HOUR: string = translate('sales.hour');
  const SHIPPING_TYPE: string = translate('sales.shipping_type');
  const COLUMN_CLIENT_NAME: string = translate('sales.client_name');
  const COLUMN_ID_CLIENT: string = translate('sales.id_client');
  const COLUMN_SEND: string = translate('sales.send');
  const MAIN_TITLE: string = translate('sales.main_title');
  const MAIN_TITLE_LABEL: string = translate('sales.main_title_label');
  const DOWNLOAD_BUTTON_TEXT: string = translate('sales.download_button_text');
  const SHIPPING_TYPE_TEXT: string = translate('product.shipping_types_label');
  const FILTER_TITLE: string = translate('store.filter_title');



  const DATEPICKER_TITLE: string = translate('sales.datepicker_title');
  const DATEPICKER_DESCRIPTION: string = translate('sales.datepicker_description');

  const CANCEL_CONFIRMATION_TEXT: string = translate('sales.confirmation_text')
    .replace('NEW_STATUS', getStatusById(selectedNewStatus ?? 0,).toUpperCase());

  const CANCEL_BUTTON_TEXT: string = translate('sales.cancel_button_text');
  const SEND_BUTTON_TEXT: string = translate('sales.send_button_text');
  const SEND_CONFIRMATION_TEXT: string = translate('sales.send_confirmation_text');
  const LOADING_TEXT: string = translate('sales.loading_text');
  const SUCCESS_STATUS_MESSAGE: string = translate('sales.success_status_message');
  const ERROR_STATUS_MESSAGE: string = translate('sales.error_status_message');
  const SUCCESS_EMAIL_MESSAGE: string = translate('sales.success_email_message');
  const ERROR_EMAIL_MESSAGE: string = translate('sales.error_email_message');
  const EXCEL_FILE_NAME: string = translate('sales.excel_file_name');
  const DOWNLOAD_TEXT: string = translate('sales.download_text');

  const INPUT_STATUS_CHANGE_ERROR: string = translate('sales.input_status_change_error');

  const FILTER_MODAL_BUTTON: string = translate('sales.filter_modal_button');
  const FILTER_MODAL_CLEAR: string = translate('sales.filter_modal_clear');
  const FILTERING_BY_TEXT: string = translate('sales.filtering_by_text');
  const NO_RESULTS_FILTER: string = translate('sales.no_result_filter');
  const NO_ORDERS: string = translate('sales.no_orders');
  const SEARCH: string = translate('product.selected_attribute_input_value');
  const STATE: string = translate('sales.state');
  const FILTERS_TEXT: string = translate('store.filters_text');

  const buttonVariant = getCSSVariableValue('--button-variant');

  /** Contenido de las modales  */
  const ContentModalProp = (): JSX.Element => {
    const ORDER_LABEL = translate('sales.order_label');
    const NAME_LABEL = translate('sales.name_label');
    const EMAIL_LABEL = translate('sales.email_label');
    const ADDRESS_LABEL = translate('sales.address_label');
    const SHIPPING_TYPE_LABEL = translate('sales.shipping_type_label');
    const TOTAL_LABEL = translate('sales.total_label');

    return selectedOrder ? (
      <Row className='g-0'>
        <InfoRow
          label={ORDER_LABEL}
          classLabel='paragraph'
          value={selectedOrder.ordCodeNum}
          classValue='paragraph badge rounded-pill bg-secondary-1 me-2'
        />
        <InfoRow label={NAME_LABEL} classLabel='paragraph' value={selectedOrder.customerName} classValue='paragraph text-end' />
        {identificationType && (
          <InfoRow
            label={`${identificationType.name}:`}
            classLabel='paragraph'
            value={selectedOrder.customerId.value}
            classValue='paragraph text-truncate text-end'
          />
        )}
        <InfoRow label={EMAIL_LABEL} classLabel='paragraph' value={selectedOrder.customerEmail} classValue='paragraph text-end' />
        <InfoRow label={ADDRESS_LABEL} classLabel='paragraph' value={selectedOrder.deliveryAddress} classValue='paragraph text-end' />
        {selectedOrder.ordShippingType && (
          <Col className='d-flex flex-fill'>
            <InfoRow label={SHIPPING_TYPE_LABEL} classLabel='paragraph' value={selectedOrder.ordShippingType.title} classValue='paragraph text-end' />
            <Icon classMain='ps-2' name={selectedOrder.ordShippingType.icon} />
          </Col>
        )}
        <InfoRow
          label={TOTAL_LABEL}
          classLabel='h5'
          value={currencyFormat(selectedOrder.totalAmount, ConfigNumberParameters.DECIMALS_ALLOWED)}
          classValue='h5 text-end'
        />
      </Row>
    ) : (
      <></>
    );
  };

  /** Contenido de fecha estimada de entrega del modal  */
  const DateModalProp = (): JSX.Element => {
    const DATE_LABEL = translate('sales.shipping_date_alert');
    const DATE_TITLE = translate('sales.shipping_date_title');
    const isSameDay = !!(startDate && endDate && formatDateToStringtwo(startDate) === formatDateToStringtwo(endDate));
    const formatDateLabel = (startDate: Date | null, endDate: Date | null, isSameDay: boolean): string => {
      return startDate && endDate
        ? isSameDay
          ? formatDateToStringtwo(startDate)
          : `${formatDateToStringtwo(startDate)} ${translate('sales.article_links_dates')} ${formatDateToStringtwo(endDate)}`
        : DATE_LABEL;
    };
    return (
      <>
        {isMobile ? (
          <Col className='d-flex flex-column justify-content-start'>
            <Col xs={12}>
              <IconList classIcon='pe-2' iconName='ico-national-shipment' title={DATE_TITLE} classTitle='text-secondary-1 p-regular fw-bold' classMain='px-3' />
            </Col>
            <Col xs={12} className='px-3 pt-2'>
              <span className='text-secondary-1 paragraph fw-bold'>{formatDateLabel(startDate, endDate, isSameDay)}</span>
            </Col>
          </Col>
        ) : (
          <Col className='d-flex justify-content-between'>
            <IconList classIcon='pe-2' iconName='ico-national-shipment' title={DATE_TITLE} classTitle='text-secondary-1 paragraph fw-bold' classMain='px-3' />
            <span className='text-secondary-1 paragraph fw-bold px-3'>{formatDateLabel(startDate, endDate, isSameDay)}</span>
          </Col>
        )}
      </>
    );
  };

  /** Interface para el componente que maneja la celda de los estados */
  interface PropsStatusCell {
    status: number;
    id: number;
    orderData: IOrderResponse;
    statusList: IStateFlow[];
  }

  /** Componente que muestra el estado de la orden */
  const StatusCell = ({ status, id, orderData, statusList }: PropsWithChildren<PropsStatusCell>) => {
    //Función que renderiza el select de acuerdo a las validaciones de los estados
    const statusOrder = (): IStateFlow[] => {
      const hasAValidShippingType: boolean = !!orderData.ordShippingType;

      if (!hasAValidShippingType || (orderData.ordShippingType && orderData.ordShippingType.id === ShippingTypesCodes.NATIONAL_SHIPMENT)) {
        //oculta el estado de error si la orden es de tipo nacional
        return statusList.filter((status) => status.shippingTypeId === ShippingTypesCodes.NATIONAL_SHIPMENT && status.id !== StatusCodes.ORDER_ERROR);
      } else {
        //oculta el estado de error si la orden es de tipo retiro en tienda
        return statusList.filter((status) => status.shippingTypeId === ShippingTypesCodes.PICK_UP && status.id !== StatusCodes.ORDER_ERROR);
      }
    };

    const statusListPerShippingType = statusOrder();

    /** mapeo de las transiciones de estado */
    const transitionMap = statusListPerShippingType.reduce((accumulator, status) => {
      //se acumula en un objeto las transiciones permitidas para cada estado
      accumulator[status.id] = new Set(status.allowedTransitions);

      //se retorna el objeto con las transiciones permitidas
      return accumulator;
    }, {});

    // función que se utilizará para actualizar este estado.
    const [selectedStatus, setSelectedStatus] = useState<number>(status);

    //verifica si un cambio de estado es valido dentro de el mapa de transiciones
    const isValidStatusChange = useCallback((currentStatus: number, newStatus: number): boolean => {
      //si el estado actual existe en el mapa de transiciones, se verifica si el nuevo estado es valido
      if (currentStatus === newStatus) return true;
      //se retorna si el nuevo estado es valido para el estado actual
      return transitionMap[currentStatus]?.has(newStatus);
    }, []);

    //manejador de cambio de estado
    const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
      //se obtiene el nuevo estado seleccionado
      const newStatus: number = parseInt(event.target.value);
      //si el nuevo estado es diferente al estado actual, se actualiza el estado seleccionado y se muestra el modal
      if (newStatus !== selectedStatus) {
        setSelectedOrderId(id);
        setSelectedOrder(orderData);
        setSelectedNewStatus(newStatus);
        setShowConfirmationStatusModal(true);
      }
    };

    // Transforma la data que viene del estado de la orden en parámetros para el InputSelect
    const optionStatusOrderData = (): ISelectOptionType[] => {
      const data: ISelectOptionType[] = [];

      statusOrder().forEach((element) => {
        data.push({
          id: element.id.toString(),
          value: element.id.toString(),
          label: element.name.toUpperCase(),
          className: 'p-regular text-secondary-2',
          disabled: !isValidStatusChange(selectedStatus, element.id),
        });
      });

      return data;
    };

    //se actualiza el estado inicial si cambia el estado de la orden
    useEffect(() => {
      setSelectedStatus(status);
    }, [status]);
    const options =
      selectedStatus === StatusCodes.ORDER_ERROR
        ? [{ id: '', value: '', label: 'ERROR', className: 'p-regular text-secondary-2' }]
        : optionStatusOrderData();
    return (
      <InputSelect
        value={selectedStatus === StatusCodes.ORDER_ERROR ? '' : selectedStatus.toString()}
        onChange={handleSelectChange}
        className='p-regular text-secondary-2'
        disabled={selectedStatus === StatusCodes.ORDER_ERROR || [StatusCodes.ORDER_DELIVERED, StatusCodes.ORDER_CANCELED].includes(selectedStatus)}
        optionData={options}
      />
    );
  };

  /** Función que limpia las fechas seleccionadas del calendario y cierra el modal de confirmacion*/
  const cleanDateAndClose = (): void => {
    setShowConfirmationStatusModal(false);
    setStartDate(null);
    setEndDate(null);
  };

  /**Valida el formulario de cambio de estado cada vez que se muestra la modal de cambio de estado */
  useLayoutEffect(() => {
    showConfirmationStatusModal && handleValidateChangeStatusReason();
  }, [showConfirmationStatusModal]);

  /** Se ejecuta cuando el usuario se loguea*/
  useEffect(() => {
    userLogued.user.token && getOrdersBack();
  }, [userLogued.user.isLogued]);

  /** Hacer scroll al tope al montar el componente */
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  /** Prepara el objeto de los estados y su transición permitida */
  useEffect(() => {
    getStatusListData();
  }, []);

  return (
    <>
      <ManagementOptionsContainer type='sales-history'>
        <MainTitle iconClass='pe-2' iconName='ico-sales' boldTitle={MAIN_TITLE} detailsText={MAIN_TITLE_LABEL} />
        <div className='bg-secondary-4 px-3 py-3 mb-3'>
          <Row className='align-items-center'>
            <Col lg={4} md={6} sm={12} className='mb-3 mb-lg-0'>
              <div className='d-flex align-items-center'>
                <InputForm
                  id='global-search'
                  iconName='ico-search'
                  firstIconName='ico-search'
                  firstIconClass='start-0 top-50 translate-middle-y ms-2 position-absolute disabled'
                  placeholder={SEARCH}
                  value={globalSearch}
                  classMain='position-relative w-100'
                  className='flex-grow-1'
                  onChange={(e) => {
                    setGlobalSearch(e.target.value);
                    applyAllFilters(unfilteredOrders, e.target.value, statusFilter, deliveryTypeFilter);
                  }}
                  lastIconName={globalSearch ? 'ico-close' : undefined}
                  lastIconClass={globalSearch ? 'end-0 top-50 translate-middle-y me-3 position-absolute cursor-pointer' : undefined}
                  lastIconClicked={() => {
                    setGlobalSearch('');
                    applyAllFilters(unfilteredOrders, '', statusFilter, deliveryTypeFilter);
                  }}
                />
              </div>
            </Col>
            <Col lg={5} md={6} sm={12} className='mb-3 mb-lg-0 d-flex align-items-center'>
              <div className='position-relative w-100'>
                <div className='d-flex align-items-center flex-wrap w-100'>
                  <span className='ms-2 text-secondary-2'>{FILTER_TITLE}</span>
                  <Icon
                    name='ico-filter'
                    classMain={`ms-2 ${unfilteredOrders.length === 0 ? 'svg-tertiary-1 cusor-default' : 'cursor-pointer svg-black-4 hover-opacity'}`}
                    onClick={(e) => {
                      if (unfilteredOrders.length > 0) {
                        e.stopPropagation();
                        openFilterModal();
                      }
                    }}
                  />
                  {(statusFilter !== null || deliveryTypeFilter !== null) && (
                    <ActiveFiltersBadges
                      statusFilter={statusFilter}
                      deliveryTypeFilter={deliveryTypeFilter}
                      uniqueShippingTypes={uniqueShippingTypes}
                      buttonVariant={buttonVariant}
                      clearFilter={clearFilter}
                      FILTERING_BY_TEXT={FILTERING_BY_TEXT}
                      FILTER_MODAL_CLEAR={FILTER_MODAL_CLEAR}
                    />
                  )}

                </div>
              </div>
            </Col>
            <Col lg={3} md={12} className='d-flex justify-content-end align-items-center'>
              <Button className={`${buttonVariant} p-title-bold ${!orders.length ? 'disabled' : ''}`} onClick={() => getOrdersDetailsBack()}>
                {DOWNLOAD_BUTTON_TEXT}
              </Button>
            </Col>
          </Row>
        </div>
        <Col className='text-break'>
          {orders.length > 0 ? (
            <TableCustom
              dataTable={orders}
              columns={columns}
              sortingState={[{ id: 'position', desc: false }]}
              visibilityState={{ id: false, position: false }}
            />
          ) : isFilterActive ? (
            <div className='text-center py-5'>
              <Icon name='ico-warning' classMain='svg-secondary-1 mb-3' />
              <h5 className='text-secondary-2'>{`${NO_RESULTS_FILTER}`}</h5>
            </div>
          ) : (
            <div className='text-center py-5'>
              <h5 className='text-secondary-2'>{NO_ORDERS}</h5>
            </div>
          )}
        </Col>
      </ManagementOptionsContainer>

      {/* Modal de filtros */}
      <ContentModal
        dialogClassName='modal-dialog-centered'
        contentClassName='rounded-4 mh-400-px d-flex justify-content-between shadow border-0'
        showModal={showFilterModal}
        isBlocked={false}
        onClose={closeFilterModalAndResetTemp}
        message={FILTERS_TEXT}
        contentColClass={8}
        classMessage='text-center px-5 text-secondary-1 h4 mb-4 fw-bold'
        form={
          <>
            {/* Función que filtra por Estado */}
            <StatusFilterOptions />
            {/* Funcion que filtra por Tipo de Entrega */}
            <DeliveryTypeFilterOptions />
          </>
        }
        buttons={
          <TwoButtonsGroup
            classContainer='pt-3 pb-1 px-0'
            firstButtonClass={buttonVariant}
            secondButtonClass='btn-primary-text-small'
            firstButtonText={CANCEL_BUTTON_TEXT}
            secondButtonText={FILTER_MODAL_BUTTON}
            firstButtonClick={closeFilterModalAndResetTemp}
            secondButtonClick={applyFilter}
          />
        }
      />

      {/* Modal de confirmación */}
      <ContentModal
        dialogClassName='modal-dialog-centered'
        contentClassName='rounded-4 mh-400-px d-flex justify-content-between shadow border-0'
        classContent='text-start p-2 bg-secondary-4 rounded-3'
        showModal={showConfirmationStatusModal}
        isBlocked
        onClose={() => setShowConfirmationStatusModal(false)}
        message={CANCEL_CONFIRMATION_TEXT}
        contentColClass={8}
        classMessage='text-center px-5 text-secondary-1 h4 mb-4 fw-bold'
        content={<ContentModalProp />}
        modalSize='lg'
        form={
          statusToValidate.includes(selectedNewStatus ?? 0) ? (
            <InputForm
              iconName='ico-error'
              className='mt-3'
              inputRef={statusReasonInputRef}
              label={INPUT_STATUS_CHANGE_LABEL}
              classLabel='p-regular-medium'
              withErrors={statusReasonInputWithError}
              messageError={statusReasonInputMessageError}
              type='textarea'
              rowsValue={3}
              onChange={handleChangeStatusReason}
            />
          ) : undefined
        }
        buttons={
          <TwoButtonsGroup
            classContainer='pt-3 pb-1 px-0'
            firstButtonClass={buttonVariant}
            secondButtonClass={`btn-primary-text-small ${statusReasonInputWithError ? 'disabled' : ''} ${selectedNewStatus === StatusCodes.ORDER_PROCESSING &&
              selectedOrder?.ordShippingType.id === ShippingTypesCodes.NATIONAL_SHIPMENT &&
              (!startDate || !endDate)
              ? 'disabled'
              : ''
              }`}
            firstButtonText={CANCEL_BUTTON_TEXT}
            secondButtonText={CHANGE_BUTTON_TEXT}
            firstButtonClick={cleanDateAndClose}
            secondButtonClick={confirmStatusChange}
          />
        }
        datePicker={
          selectedNewStatus === StatusCodes.ORDER_PROCESSING && selectedOrder?.ordShippingType.id === ShippingTypesCodes.NATIONAL_SHIPMENT ? (
            <DatePickerRange onDateChange={handleDateChange} />
          ) : null
        }
        datePickerTitle={selectedNewStatus === StatusCodes.ORDER_PROCESSING ? DATEPICKER_TITLE : ''}
        datePickerDescription={selectedNewStatus === StatusCodes.ORDER_PROCESSING ? DATEPICKER_DESCRIPTION : ''}
        deliveryDate={<DateModalProp />}
      />

      {/* Modal de envío de correo */}
      <ContentModal
        classContent='text-start p-2 bg-secondary-4 rounded-3'
        classMessage='text-center px-5 text-secondary-1 h4 mb-4 fw-bold'
        dialogClassName='modal-dialog-centered'
        contentClassName='rounded-4 mh-400-px d-flex justify-content-between shadow border-0'
        modalSize='lg'
        contentColClass={8}
        showModal={showSendEmailModal}
        isBlocked
        onClose={() => setShowSendEmailModal(false)}
        message={SEND_CONFIRMATION_TEXT}
        content={<ContentModalProp />}
        buttons={
          <TwoButtonsGroup
            classContainer='pt-3 pb-1 px-0'
            firstButtonClass={buttonVariant}
            secondButtonClass='btn-primary-text-small'
            firstButtonText={CANCEL_BUTTON_TEXT}
            secondButtonText={SEND_BUTTON_TEXT}
            firstButtonClick={() => setShowSendEmailModal(false)}
            secondButtonClick={sendOrder}
          />
        }
      />

      <InfoModal
        showModal={showDownloadModal}
        handleShowModal={showLoading}
        image={
          <Image
            src={DOWNLOAD_IMAGE_URL}
            width={200}
            alt='gif'
            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
              e.currentTarget.src = ErrorImage;
            }}
          />
        }
        message={<span className='p-large-bold mt-3'>{DOWNLOAD_TEXT}</span>}
      />
      <InfoModal
        showModal={showLoadingEmailModal}
        handleShowModal={handleSendEmail}
        image={
          <Image
            src={LOADING_IMAGE_URL}
            width={250}
            alt='gif'
            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
              e.currentTarget.src = ErrorImage;
            }}
          />
        }
        message={<span className='p-large-bold mt-3'>{LOADING_TEXT}</span>}
      />

      {alertStatus && (
        <AlertModal
          hideAlert={() => setAlertStatus(false)}
          type={alertStatusType}
          hide={6000}
          content={alertStatusType === 'success' ? SUCCESS_STATUS_MESSAGE : ERROR_STATUS_MESSAGE}
        />
      )}
      {alertEmail && (
        <AlertModal
          hideAlert={() => setAlertEmail(false)}
          type={alertEmailType}
          hide={6000}
          content={alertEmailType === 'success' ? SUCCESS_EMAIL_MESSAGE : ERROR_EMAIL_MESSAGE}
        />
      )}
    </>
  );
};

export default SalesHistory;
